import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  Shield, CheckCircle, AlertTriangle, FileText, Wallet,
  ChevronRight, Lock, User, Scale, Info, XCircle, ArrowLeft,
  Upload, Building, DollarSign, Briefcase, PenTool, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserApplicationPortal = () => {
  const { user, token } = useAuth();
  const { account, connectWallet } = useWeb3();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [disclosures, setDisclosures] = useState(null);
  const [acceptedDisclosures, setAcceptedDisclosures] = useState([]);
  const [acceptedRisks, setAcceptedRisks] = useState([]);
  const [accreditationStatus, setAccreditationStatus] = useState(null);
  
  // Application form data
  const [formData, setFormData] = useState({
    fullName: '',
    verificationMethod: 'income',
    entityName: '',
    isEntity: false,
    signature: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchDisclosures();
    checkAccreditationStatus();
  }, [token]);

  const fetchDisclosures = async () => {
    try {
      const response = await fetch(`${API}/compliance/disclosures`);
      const data = await response.json();
      setDisclosures(data);
    } catch (error) {
      console.error('Error fetching disclosures:', error);
    }
  };

  const checkAccreditationStatus = async () => {
    try {
      const response = await fetch(`${API}/accreditation/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setAccreditationStatus(data);
      
      if (data.is_verified) {
        setStep(5); // Skip to complete
      }
    } catch (error) {
      console.error('Error checking accreditation:', error);
    }
  };

  const toggleDisclosure = (disclosure) => {
    setAcceptedDisclosures(prev => 
      prev.includes(disclosure) 
        ? prev.filter(d => d !== disclosure)
        : [...prev, disclosure]
    );
  };

  const toggleRisk = (risk) => {
    setAcceptedRisks(prev => 
      prev.includes(risk) 
        ? prev.filter(r => r !== risk)
        : [...prev, risk]
    );
  };

  const acceptAllDisclosures = () => {
    if (disclosures?.disclosures) {
      setAcceptedDisclosures(disclosures.disclosures);
    }
  };

  const acceptAllRisks = () => {
    if (disclosures?.risk_acknowledgments) {
      setAcceptedRisks(disclosures.risk_acknowledgments);
    }
  };

  const submitAccreditationApplication = async () => {
    if (!formData.fullName) {
      toast.error('Please enter your full legal name');
      return;
    }

    setLoading(true);
    try {
      // Create accreditation application
      const response = await fetch(`${API}/accreditation/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          verification_method: formData.verificationMethod,
          entity_name: formData.entityName,
          is_entity: formData.isEntity
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Application submitted successfully!');
        setStep(4);
      } else {
        toast.error(data.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const submitSignature = async () => {
    if (!formData.signature) {
      toast.error('Please sign the agreement');
      return;
    }

    setLoading(true);
    try {
      // Create signature request
      const requestRes = await fetch(`${API}/esignature/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          agreement_type: 'participation_agreement'
        })
      });
      
      const requestData = await requestRes.json();
      
      if (requestData.success) {
        // Sign the agreement
        const signRes = await fetch(`${API}/esignature/sign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            request_id: requestData.request_id,
            signature_data: formData.signature,
            signature_type: 'typed'
          })
        });
        
        const signData = await signRes.json();
        
        if (signData.success) {
          toast.success('Agreement signed successfully!');
          await submitAccreditationApplication();
        } else {
          toast.error(signData.error || 'Failed to sign agreement');
        }
      }
    } catch (error) {
      console.error('Error signing agreement:', error);
      toast.error('Failed to sign agreement');
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = acceptedDisclosures.length === disclosures?.disclosures?.length;
  const canProceedStep2 = acceptedRisks.length === disclosures?.risk_acknowledgments?.length;
  const canProceedStep3 = formData.fullName && formData.verificationMethod;

  const verificationMethods = [
    {
      id: 'income',
      name: 'Income Verification',
      icon: DollarSign,
      description: 'Tax returns showing $200K+ individual / $300K+ joint income',
      documents: ['Last 2 years tax returns', 'W-2 or 1099 forms']
    },
    {
      id: 'net_worth',
      name: 'Net Worth Verification',
      icon: Building,
      description: 'Net worth exceeding $1M (excluding primary residence)',
      documents: ['Bank statements', 'Brokerage statements', 'Real estate appraisals']
    },
    {
      id: 'professional',
      name: 'Professional Certification',
      icon: Briefcase,
      description: 'FINRA Series 7, 65, or 82 license holder',
      documents: ['FINRA BrokerCheck report', 'License documentation']
    },
    {
      id: 'entity',
      name: 'Entity Verification',
      icon: Building,
      description: 'Entity with $5M+ in assets',
      documents: ['Formation documents', 'Financial statements']
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a1628] py-12 px-4" data-testid="user-application-portal">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/investor-guide">
            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Guide
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-[#b8860b]" />
              Investor Application
            </h1>
            <p className="text-gray-400 mt-1">Complete your accreditation to access the platform</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8 overflow-x-auto pb-2">
          {[
            { num: 1, label: 'Disclosures' },
            { num: 2, label: 'Risks' },
            { num: 3, label: 'Verification' },
            { num: 4, label: 'Review' },
            { num: 5, label: 'Complete' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                  step >= s.num ? 'bg-[#b8860b] text-white' : 'bg-[#1a2d4a] text-gray-500'
                }`}>
                  {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-1 ${step >= s.num ? 'text-[#b8860b]' : 'text-gray-500'}`}>
                  {s.label}
                </span>
              </div>
              {idx < 4 && (
                <div className={`w-12 h-1 mx-2 ${step > s.num ? 'bg-[#b8860b]' : 'bg-[#1a2d4a]'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Already Verified */}
        {accreditationStatus?.is_verified && step !== 5 && (
          <Card className="bg-green-900/20 border-green-500/30 mb-6">
            <CardContent className="py-4 flex items-center gap-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-white font-medium">You are already verified!</p>
                <p className="text-gray-400 text-sm">Verification method: {accreditationStatus.verification_method}</p>
              </div>
              <Button className="ml-auto bg-[#b8860b] hover:bg-[#9a7209]" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Disclosures */}
        {step === 1 && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" />
                Step 1: Review Legal Disclosures
              </CardTitle>
              <CardDescription>Please read and accept all required disclosures</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Legal Notice */}
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg border border-[#2a3d5a]">
                <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                  <Scale className="w-4 h-4 text-yellow-400" />
                  Legal Notice
                </h3>
                <p className="text-gray-300 text-sm whitespace-pre-line">
                  {disclosures?.legal_notice || 'Loading...'}
                </p>
              </div>

              {/* Execution Model */}
              <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <h3 className="text-blue-400 font-medium mb-2">Execution Model</h3>
                <p className="text-white font-medium">{disclosures?.execution_model?.name}</p>
                <p className="text-gray-300 text-sm mt-1">{disclosures?.execution_model?.description}</p>
                <ul className="mt-2 space-y-1">
                  {disclosures?.execution_model?.key_points?.map((point, i) => (
                    <li key={i} className="text-gray-400 text-sm flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Disclosures Checklist */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-medium">Required Disclosures</h3>
                  <Button variant="ghost" size="sm" onClick={acceptAllDisclosures} className="text-[#b8860b]">
                    Accept All
                  </Button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {disclosures?.disclosures?.map((disclosure, i) => (
                    <div 
                      key={i}
                      onClick={() => toggleDisclosure(disclosure)}
                      className={`p-3 rounded-lg cursor-pointer flex items-start gap-3 transition-colors ${
                        acceptedDisclosures.includes(disclosure)
                          ? 'bg-green-900/20 border border-green-500/30'
                          : 'bg-[#1a2d4a]/50 border border-[#2a3d5a] hover:border-[#3a4d6a]'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                        acceptedDisclosures.includes(disclosure) ? 'bg-green-500' : 'bg-[#2a3d5a]'
                      }`}>
                        {acceptedDisclosures.includes(disclosure) && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-gray-300 text-sm">{disclosure}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                className="w-full bg-[#b8860b] hover:bg-[#9a7209]"
                disabled={!canProceedStep1}
                onClick={() => setStep(2)}
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Risk Acknowledgments */}
        {step === 2 && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Step 2: Risk Acknowledgments
              </CardTitle>
              <CardDescription>Acknowledge the risks associated with the platform</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-medium">Risk Acknowledgments</h3>
                  <Button variant="ghost" size="sm" onClick={acceptAllRisks} className="text-[#b8860b]">
                    Accept All
                  </Button>
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {disclosures?.risk_acknowledgments?.map((risk, i) => (
                    <div 
                      key={i}
                      onClick={() => toggleRisk(risk)}
                      className={`p-3 rounded-lg cursor-pointer flex items-start gap-3 transition-colors ${
                        acceptedRisks.includes(risk)
                          ? 'bg-yellow-900/20 border border-yellow-500/30'
                          : 'bg-[#1a2d4a]/50 border border-[#2a3d5a] hover:border-[#3a4d6a]'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                        acceptedRisks.includes(risk) ? 'bg-yellow-500' : 'bg-[#2a3d5a]'
                      }`}>
                        {acceptedRisks.includes(risk) && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <span className="text-gray-300 text-sm">{risk}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 border-[#2a3d5a]" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                  disabled={!canProceedStep2}
                  onClick={() => setStep(3)}
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Verification Method & Application */}
        {step === 3 && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-purple-400" />
                Step 3: Accreditation Verification
              </CardTitle>
              <CardDescription>Select your verification method and complete your application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Full Legal Name *</label>
                <Input 
                  placeholder="Enter your full legal name"
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                />
              </div>

              {/* Entity Toggle */}
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox"
                  id="isEntity"
                  checked={formData.isEntity}
                  onChange={(e) => setFormData(prev => ({ ...prev, isEntity: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="isEntity" className="text-gray-300 text-sm">
                  I am applying on behalf of an entity
                </label>
              </div>

              {formData.isEntity && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Entity Name</label>
                  <Input 
                    placeholder="Enter entity name"
                    value={formData.entityName}
                    onChange={(e) => setFormData(prev => ({ ...prev, entityName: e.target.value }))}
                    className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                  />
                </div>
              )}

              {/* Verification Methods */}
              <div>
                <label className="block text-sm text-gray-400 mb-3">Verification Method *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {verificationMethods.map((method) => (
                    <div 
                      key={method.id}
                      onClick={() => setFormData(prev => ({ ...prev, verificationMethod: method.id }))}
                      className={`p-4 rounded-lg cursor-pointer transition-colors ${
                        formData.verificationMethod === method.id
                          ? 'bg-[#b8860b]/20 border-2 border-[#b8860b]'
                          : 'bg-[#1a2d4a]/50 border border-[#2a3d5a] hover:border-[#3a4d6a]'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <method.icon className={`w-5 h-5 ${formData.verificationMethod === method.id ? 'text-[#b8860b]' : 'text-gray-400'}`} />
                        <span className="text-white font-medium">{method.name}</span>
                      </div>
                      <p className="text-gray-400 text-xs mb-2">{method.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {method.documents.map((doc, i) => (
                          <span key={i} className="text-xs bg-[#2a3d5a] text-gray-300 px-2 py-0.5 rounded">
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* E-Signature */}
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg border border-[#2a3d5a]">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-[#b8860b]" />
                  Electronic Signature
                </h3>
                <p className="text-gray-400 text-sm mb-3">
                  By typing your full name below, you certify that:
                </p>
                <ul className="text-gray-400 text-sm space-y-1 mb-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                    You are an accredited investor as defined by SEC Rule 501
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                    All information provided is accurate and complete
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                    You have read and agree to all disclosures and risks
                  </li>
                </ul>
                <Input 
                  placeholder="Type your full legal name to sign"
                  value={formData.signature}
                  onChange={(e) => setFormData(prev => ({ ...prev, signature: e.target.value }))}
                  className="bg-[#0d1d35] border-[#2a3d5a] text-white font-serif italic"
                />
                {formData.signature && formData.signature === formData.fullName && (
                  <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Signature matches name
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 border-[#2a3d5a]" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-[#b8860b] hover:bg-[#9a7209]"
                  disabled={!canProceedStep3 || !formData.signature || formData.signature !== formData.fullName || loading}
                  onClick={submitSignature}
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Application Submitted */}
        {step === 4 && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Application Submitted!</h2>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Your accreditation application is being reviewed. You will receive an email notification once your verification is complete.
              </p>
              
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg mb-6 text-left max-w-md mx-auto">
                <h3 className="text-white font-medium mb-3">Application Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Name</span>
                    <span className="text-white">{formData.fullName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Method</span>
                    <span className="text-white capitalize">{formData.verificationMethod.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status</span>
                    <span className="text-yellow-400">Pending Review</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-4">
                <Link to="/investor-guide">
                  <Button variant="outline" className="border-[#2a3d5a]">
                    View Guide
                  </Button>
                </Link>
                <Button className="bg-[#b8860b] hover:bg-[#9a7209]" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Verified Complete */}
        {step === 5 && (
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Verification Complete!</h2>
              <p className="text-gray-400 mb-6">
                You are now verified as an accredited participant.
              </p>
              
              <div className="p-4 bg-[#1a2d4a]/50 rounded-lg mb-6 text-left max-w-md mx-auto">
                <h3 className="text-white font-medium mb-3">You can now:</h3>
                <ul className="space-y-2">
                  <li className="text-gray-300 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Receive and hold BETH tokens
                  </li>
                  <li className="text-gray-300 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Use Credit Enhancement services
                  </li>
                  <li className="text-gray-300 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Create PAS agreements for debt settlement
                  </li>
                  <li className="text-gray-300 text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    View portfolio analytics
                  </li>
                </ul>
              </div>

              <Button className="bg-[#b8860b] hover:bg-[#9a7209]" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UserApplicationPortal;
